import type { EninCompanySearchHit, EninLookupInput, EninLookupResult } from "@repo/shared/types";
import {
  useEninCompanySearch,
  useEninLookupFlowInstanceValue,
  useScreenEninPersons,
  useSearchAndSaveEninLookup,
} from "@/lib/hooks/use-flow-instances/use-enin-lookup";
import { EninLookupWidget } from "../enin-lookup/enin-lookup-widget";

type WizardEninLookupProps = {
  instanceId: string;
  clientName: string | null;
  clientCountry: string | null;
  clientOrgId: string | null;
  disabled: boolean;
  style?: "none" | "border" | "box";
  title?: string;
  description?: string;
  language: string;
  hasValidationError?: boolean;
};

export function WizardEninLookup({
  instanceId,
  clientName,
  clientCountry,
  clientOrgId,
  disabled,
  style,
  title,
  description,
  language,
  hasValidationError,
}: WizardEninLookupProps) {
  const { calculatedData, isLoading: valueLoading } = useEninLookupFlowInstanceValue(instanceId);
  const searchAndSave = useSearchAndSaveEninLookup(instanceId);
  const companySearch = useEninCompanySearch(instanceId);
  const screenPersons = useScreenEninPersons(instanceId);

  const handleRunLookup = async (input: EninLookupInput): Promise<EninLookupResult> => {
    const result = await searchAndSave.mutateAsync(input);
    if (!result.success || !result.calculatedData) {
      throw new Error(result.error || "Lookup failed");
    }
    return JSON.parse(result.calculatedData) as EninLookupResult;
  };

  const handleScreenPersons = async (): Promise<EninLookupResult> => {
    const result = await screenPersons.mutateAsync();
    if (!result.success || !result.calculatedData) {
      throw new Error(result.error || "Screening failed");
    }
    return JSON.parse(result.calculatedData) as EninLookupResult;
  };

  const handleSearchCompanies = async (name: string): Promise<EninCompanySearchHit[]> => {
    const result = await companySearch.mutateAsync(name);
    if (!result.success) {
      throw new Error(result.error || "Search failed");
    }
    return result.results;
  };

  if (valueLoading) return null;

  return (
    <EninLookupWidget
      clientName={clientName}
      clientCountry={clientCountry}
      clientOrgId={clientOrgId}
      calculatedData={calculatedData}
      onRunLookup={handleRunLookup}
      onSearchCompanies={handleSearchCompanies}
      onScreenPersons={handleScreenPersons}
      disabled={disabled}
      style={style}
      title={title}
      description={description}
      language={language}
      hasValidationError={hasValidationError}
    />
  );
}
